import React, { useEffect } from 'react'

import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { detect } from 'detect-browser'

import NoSsr from 'common/components/NoSsr'
import { GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import {
  ActionButton,
  Container,
  HeroWrap,
  ImagePlaceholder,
  InfoWrap,
  Overlay,
  SubTitle,
  Title,
  Video,
} from 'artkive/pages/Home/HeroSection.style'
import routes from 'artkive/utils/routes'

import VideoPosterTablet from 'images/ecom/home/video-poster/1080p.jpg'
import VideoPosterMobile from 'images/ecom/home/video-poster/cropped-720p.jpg'
import VideoPosterDesktop from 'images/ecom/home/video-poster/poster.jpg'

const VIDEO_TAG_ID = 'hero-video'

const browser = detect()

const HeroSection = () => {
  const muiTheme = useTheme()
  const isMobile = useMobileBreakpoint()

  const tabletBreakpoint = muiTheme.breakpoints.values.md // 960
  const mobileBreakpoint = muiTheme.breakpoints.values.sm // 600
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up(tabletBreakpoint))
  const isTablet = useMediaQuery(muiTheme.breakpoints.up(mobileBreakpoint))

  const isSafariBrowser = browser?.name === 'safari' && browser?.os === 'Mac OS'

  // iOS pauses autoplay videos on app/background switch —
  // this attempts to resume playback on return
  useEffect(() => {
    if (!isMobile) return

    function handleVisibilityChange() {
      const video = document.getElementById(VIDEO_TAG_ID)
      if (video && document.visibilityState === 'visible') {
        setTimeout(() => {
          video.play()
        }, 50)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isMobile])

  return (
    <HeroWrap>
      <Container>
        <Overlay />
        <ImagePlaceholder />
        <NoSsr>
          <Video
            id={VIDEO_TAG_ID}
            autoPlay={true}
            muted={true}
            loop={true}
            playsInline={true}
            preload={'auto'}
            poster={isDesktop ? VideoPosterDesktop : isTablet ? VideoPosterTablet : VideoPosterMobile}
          >
            {isDesktop && !isSafariBrowser && (
              <source
                src={'https://artkive-public-assets.s3.us-east-1.amazonaws.com/videos/hero-video-4K.webm'}
                type={'video/webm'}
                media={`(min-width: ${tabletBreakpoint}px)`}
              />
            )}
            <source
              src={'https://artkive-public-assets.s3.us-east-1.amazonaws.com/videos/hero-video-4K.mp4'}
              type={'video/mp4'}
              media={`(min-width: ${tabletBreakpoint}px)`}
            />
            <source
              src={'https://artkive-public-assets.s3.us-east-1.amazonaws.com/videos/hero-video-1080p.mp4'}
              type={'video/mp4'}
              media={`(min-width: ${mobileBreakpoint}px)`}
            />
            <source
              src={'https://artkive-public-assets.s3.us-east-1.amazonaws.com/videos/hero-video-720p.mp4'}
              type={'video/mp4'}
            />
          </Video>
        </NoSsr>
        <InfoWrap>
          <Title variant={'h1'}>
            The #1 solution<br />for preserving and celebrating kids&apos; art!
          </Title>
          <SubTitle variant={isMobile ? 'body1' : 'subtitle1'}>
            We transform your piles of kid art into Art Books and Framed Mosaics.
          </SubTitle>
          <ActionButton
            color={'primary'}
            variant={'contained'}
            href={routes.boxCheckout}
            trackerEvent={GET_BOX_CLICK}
            trackerOptions={{ section: 'home_start_banner' }}
          >
            Get My Box
          </ActionButton>
        </InfoWrap>
      </Container>
    </HeroWrap>
  )
}

export default HeroSection
